import React, { useState } from 'react';
import { Tag, Modal, Form, Input, Button, Upload, message } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';

interface EmailRecipient {
  firstName: string;
  lastName: string;
  email: string;
}

interface EmailInputProps {
  value?: EmailRecipient[];
  onChange?: (recipients: EmailRecipient[]) => void;
}

const EmailInput: React.FC<EmailInputProps> = ({ value = [], onChange }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  const handleAddEmail = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const newRecipient: EmailRecipient = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      };
      const newValue = [...value, newRecipient];
      onChange?.(newValue);
      form.resetFields();
      setIsModalVisible(false);
    } catch (error) {
      // Form validation failed - no need to show additional error message
      // as Ant Design Form will display field-level validation errors
      console.log('Form validation failed:', error);
    }
  };

  const handleRemoveEmail = (email: string) => {
    const newValue = value.filter((recipient) => recipient.email !== email);
    onChange?.(newValue);
  };

  const handleCSVUpload: UploadProps['beforeUpload'] = (file: RcFile) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const validRecipients = results.data
          .filter((row: any) => row['First Name'] && row['Last Name'] && row.email)
          .map((row: any) => ({
            firstName: row['First Name'],
            lastName: row['Last Name'],
            email: row.email,
          }));

        if (validRecipients.length > 0) {
          const newValue = [...value, ...validRecipients];
          onChange?.(newValue);
          message.success(`Successfully imported ${validRecipients.length} recipients`);
        } else {
          message.error('No valid recipients found in CSV');
        }
      },
      error: () => {
        message.error('Error parsing CSV file');
      },
    });
    return false;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 min-h-[42px] p-1 border rounded-md bg-white">
        {value.map((recipient) => (
          <Tag
            key={recipient.email}
            closable
            onClose={() => handleRemoveEmail(recipient.email)}
            className="m-1 text-sm bg-cyan-50 border-cyan-200 text-cyan-800"
          >
            {`${recipient.firstName} ${recipient.lastName} <${recipient.email}>`}
          </Tag>
        ))}
      </div>
      
      <div className="flex gap-2">
        <Button 
          type="default"
          onClick={handleAddEmail} 
          icon={<PlusOutlined />}
          className="border-cyan-500 text-cyan-600 hover:text-cyan-500 hover:border-cyan-400"
        >
          Add Recipient
        </Button>
        
        <Upload
          accept=".csv"
          showUploadList={false}
          beforeUpload={handleCSVUpload}
        >
          <Button 
            icon={<UploadOutlined />}
            className="border-cyan-500 text-cyan-600 hover:text-cyan-500 hover:border-cyan-400"
          >
            Import CSV
          </Button>
        </Upload>
      </div>

      <Modal
        title={<span className="text-cyan-800">Add Recipient</span>}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okButtonProps={{
          className: 'bg-cyan-600 hover:bg-cyan-500 border-cyan-600 hover:border-cyan-500'
        }}
        cancelButtonProps={{
          className: 'hover:text-cyan-500 hover:border-cyan-400'
        }}
        className="[&_.ant-input]:rounded-[6px] [&_.ant-input]:border-[#d9d9d9] [&_.ant-input:hover]:border-[#4096ff] [&_.ant-input:focus]:border-[#4096ff] [&_.ant-input:focus-within]:border-[#4096ff] [&_.ant-input:focus]:shadow-[0_0_0_2px_rgba(5,145,255,0.1)] [&_.ant-input-focused]:border-[#4096ff] [&_.ant-input-focused]:shadow-[0_0_0_2px_rgba(5,145,255,0.1)]"
      >
        <Form
          form={form}
          layout="vertical"
          className="pt-4"
        >
          <Form.Item
            name="firstName"
            label={<span className="text-gray-700">First Name</span>}
            rules={[{ required: true, message: 'Please input first name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={<span className="text-gray-700">Last Name</span>}
            rules={[{ required: true, message: 'Please input last name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={<span className="text-gray-700">Email</span>}
            rules={[
              { required: true, message: 'Please input email!' },
              { type: 'email', message: 'Please input a valid email!' }
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailInput; 